import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckSquare,
  Square,
  Trash2,
  X,
  Pencil,
  Layers,
  Grid3x3,
  Rows3,
} from 'lucide-react';
import { getSet, getSetImages, removeImageFromSet, deleteSet } from '../api/sets';
import { useFilterStore } from '../stores/filterStore';
import { FilterBar } from '../components/ui/FilterBar';
import { CopyLinksButton } from '../components/ui/CopyLinksButton';
import { DownloadZipButton } from '../components/ui/DownloadZipButton';
import { useScrollDirection } from '../hooks/useScrollDirection';
import { MasonryGrid } from '../components/gallery/MasonryGrid';
import { ImageCard } from '../components/gallery/ImageCard';
import { UserPair } from '../components/ui/UserChip';
import { SearchPill } from '../components/ui/SearchPill';
import { imageUrl } from '../api/client';
import { timeAgo } from '../utils/timeAgo';
import type { SetImage } from '../api/setsTypes';
import type {
  GalleryFilters,
  ImageSummary,
  StarFilter,
} from '../api/types';

// SetView — visar bilder från ett set, eventuellt blandat från flera shoots.
// Två view-lägen: Grouped (dividers per shoot) eller Custom (mixed sort_order).
type ViewMode = 'grouped' | 'custom';

export default function SetView() {
  const { setId } = useParams<{ setId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const filters = useFilterStore();
  const toolbarHidden = useScrollDirection(120);

  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editOpen, setEditOpen] = useState(false);

  const setQuery = useQuery({
    queryKey: ['set', setId],
    queryFn: () => getSet(setId!),
    enabled: !!setId,
  });

  const imagesQuery = useQuery({
    queryKey: ['set', setId, 'images'],
    queryFn: () =>
      getSetImages(setId!, {
        sort: viewMode === 'grouped' ? 'taken_at' : 'sort_order',
      }),
    enabled: !!setId,
  });

  // Re-fetch när viewMode ändras (annan sort)
  useEffect(() => {
    if (setId) {
      queryClient.invalidateQueries({ queryKey: ['set', setId, 'images'] });
    }
  }, [viewMode, setId, queryClient]);

  // Global Cmd+K → /search (samma som Hub/Gallery)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigate('/search');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const set = setQuery.data;
  const allImages = imagesQuery.data ?? [];

  // Klient-side filter: rating + favorite (samma som Gallery — vi gör det
  // lokalt eftersom set-images-endpointen inte stödjer dessa filter än).
  const images = useMemo(() => filterImages(allImages, filters), [allImages, filters]);

  // Gruppera per shoot för "Grouped"-läget
  const groupedByShoot = useMemo(() => {
    const groups = new Map<string, { name: string; images: SetImage[] }>();
    for (const img of images) {
      const key = img.shootId;
      if (!groups.has(key)) {
        groups.set(key, {
          name: img.shootName ?? 'Unknown shoot',
          images: [],
        });
      }
      groups.get(key)!.images.push(img);
    }
    return Array.from(groups.entries()).map(([id, g]) => ({
      shootId: id,
      shootName: g.name,
      images: g.images,
    }));
  }, [images]);

  const removeMutation = useMutation({
    mutationFn: (imageIds: string[]) =>
      Promise.all(imageIds.map((id) => removeImageFromSet(setId!, id))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['set', setId] });
      queryClient.invalidateQueries({ queryKey: ['set', setId, 'images'] });
      queryClient.invalidateQueries({ queryKey: ['hub'] });
      setSelectMode(false);
      setSelected(new Set());
    },
  });

  const deleteSetMutation = useMutation({
    mutationFn: () => deleteSet(setId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hub'] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
      navigate('/');
    },
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const accent = set?.color ?? '#5fa8d3';
  const shootCount = groupedByShoot.length;

  // ── Map av shoot-id → färg för "source"-pillen på bilder.
  // Backend levererar shootColor om den finns; annars härleder vi från
  // shoot-id deterministiskt (hash) så samma shoot alltid får samma färg.
  const shootColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const img of allImages) {
      if (!map.has(img.shootId)) {
        map.set(img.shootId, img.shootColor ?? hashColor(img.shootId));
      }
    }
    return map;
  }, [allImages]);

  return (
    <div className="min-h-screen min-h-[100dvh]" style={{ ['--set-accent' as string]: accent }}>
      {/* Top nav — back/breadcrumb vänster, global search center, user-pair höger */}
      <nav className="sticky top-0 z-50 flex items-center gap-3 sm:gap-5 px-4 sm:px-7 py-3 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border h-[58px]">
        <div className="flex items-center gap-3 sm:gap-5 min-w-0 flex-shrink">
          <Link
            to="/"
            className="text-loupe-text-3 hover:text-loupe-text text-xs uppercase tracking-[0.1em] py-2 inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Hub</span>
          </Link>
          <div className="flex items-center gap-3 text-sm min-w-0">
            <span className="hidden sm:inline text-loupe-text-3 text-xs uppercase tracking-[0.1em]">
              Set
            </span>
            <span className="hidden sm:inline text-loupe-text-3">·</span>
            <span className="font-display font-semibold text-base truncate inline-flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: accent }}
              />
              {set?.name ?? '…'}
            </span>
          </div>
        </div>

        {/* Global search */}
        <div className="flex-1 flex items-center justify-end min-w-0">
          <SearchPill />
        </div>

        <UserPair size="sm" />
      </nav>

      {/* Set header (banner) */}
      <header
        className="relative px-5 sm:px-10 pt-8 pb-7 border-b border-loupe-border overflow-hidden"
        style={{
          background: `linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.6) 100%), linear-gradient(135deg, ${accent} 0%, transparent 55%)`,
        }}
      >
        <div className="relative max-w-[1600px] mx-auto grid gap-7 grid-cols-1 sm:grid-cols-[auto_1fr_auto] items-center">
          {/* Cover collage */}
          <div
            className="rounded-2xl overflow-hidden shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7)] flex-shrink-0 bg-loupe-bg-3"
            style={{
              width: 180,
              height: 180,
            }}
          >
            {set?.coverImageId ? (
              <img
                src={imageUrl(set.coverImageId, 'preview')}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${accent} 0%, transparent 60%), #1a1a1a` }}
              >
                <span className="font-display text-6xl text-white/25">⊞</span>
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="min-w-0">
            <div className="text-[11px] text-loupe-text-3 tracking-[0.18em] uppercase mb-1.5 flex items-center gap-2">
              <span style={{ color: accent }}>⊞</span>
              Set
              {set?.lastActivityAt ? (
                <>
                  <span className="text-loupe-text-3">·</span>
                  Updated {timeAgo(set.lastActivityAt)}
                </>
              ) : null}
            </div>
            <h1 className="font-display font-normal text-4xl sm:text-5xl md:text-[56px] tracking-[-0.03em] leading-[0.98] mb-3">
              {set?.name ?? '…'}
            </h1>
            {set?.description ? (
              <div className="text-sm text-loupe-text-2 leading-relaxed max-w-[640px] mb-4">
                {set.description}
              </div>
            ) : null}
            {set?.tags && set.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-3.5">
                {set.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 bg-white/5 text-white/70 border border-white/10 rounded-full text-[11px]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="flex items-center gap-4 text-xs text-loupe-text-2 flex-wrap">
              <span>
                <strong className="text-loupe-text font-medium tabular-nums">
                  {set?.imageCount ?? 0}
                </strong>{' '}
                frames
              </span>
              {shootCount > 0 ? (
                <>
                  <span className="w-px h-3 bg-loupe-border-2" />
                  <span>
                    from{' '}
                    <strong className="text-loupe-text font-medium tabular-nums">
                      {shootCount}
                    </strong>{' '}
                    shoot{shootCount === 1 ? '' : 's'}
                  </span>
                </>
              ) : null}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2.5 items-stretch sm:items-end">
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="px-4 py-2.5 bg-loupe-bg-2/70 backdrop-blur-xl border border-loupe-border-2 text-loupe-text rounded-full text-xs font-medium tracking-wider cursor-pointer transition-colors inline-flex items-center gap-2 whitespace-nowrap min-h-[40px] hover:border-loupe-text-3"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit set
            </button>
          </div>
        </div>
      </header>

      {/* Sticky toolbar: filter + actions */}
      <div
        className={[
          'sticky top-[58px] z-[45] transition-transform duration-200 ease-out',
          toolbarHidden ? '-translate-y-full' : 'translate-y-0',
        ].join(' ')}
      >
        <FilterBar />

        <div className="flex items-center gap-2 flex-wrap px-4 sm:px-7 py-2.5 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border">
          {/* Select toggle */}
          <button
            onClick={() => {
              setSelectMode((m) => !m);
              if (selectMode) setSelected(new Set());
            }}
            className={[
              'inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs min-h-[36px] transition-colors border',
              selectMode
                ? 'bg-henrik text-white border-henrik'
                : 'bg-loupe-bg-2 text-loupe-text border-loupe-border hover:border-loupe-text-2',
            ].join(' ')}
          >
            {selectMode ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {selectMode ? `${selected.size} selected` : 'Select'}
          </button>

          <span className="text-[11px] tracking-[0.1em] uppercase text-loupe-text-3 tabular-nums">
            {imagesQuery.isLoading
              ? '…'
              : `${images.length} ${images.length === 1 ? 'image' : 'images'}`}
          </span>

          {/* View toggle */}
          <div className="ml-2 flex bg-loupe-bg-2 border border-loupe-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode('grouped')}
              className={[
                'px-2.5 py-2 text-[11px] min-h-[34px] inline-flex items-center gap-1.5 transition-colors',
                viewMode === 'grouped'
                  ? 'bg-white text-black'
                  : 'text-loupe-text-2 hover:text-loupe-text',
              ].join(' ')}
              title="Grouped by shoot"
            >
              <Rows3 className="w-3 h-3" />
              Grouped
            </button>
            <button
              type="button"
              onClick={() => setViewMode('custom')}
              className={[
                'px-2.5 py-2 text-[11px] min-h-[34px] inline-flex items-center gap-1.5 transition-colors',
                viewMode === 'custom'
                  ? 'bg-white text-black'
                  : 'text-loupe-text-2 hover:text-loupe-text',
              ].join(' ')}
              title="Custom order"
            >
              <Grid3x3 className="w-3 h-3" />
              Custom
            </button>
          </div>

          <div className="hidden sm:flex flex-1" />

          {/* Copy + download — exakt samma som Gallery */}
          <CopyLinksButton
            imageIds={
              selectMode && selected.size > 0
                ? images.filter((i) => selected.has(i.id)).map((i) => i.id)
                : images.map((i) => i.id)
            }
          />
          <DownloadZipButton
            imageIds={
              selectMode && selected.size > 0
                ? images.filter((i) => selected.has(i.id)).map((i) => i.id)
                : images.map((i) => i.id)
            }
            filenameBase={
              set?.name ? `loupe-set-${set.name.replace(/\s+/g, '-').toLowerCase()}` : 'loupe-set'
            }
          />
        </div>
      </div>

      {/* Body */}
      <div className="px-2.5 sm:px-7 pt-4 pb-32">
        {imagesQuery.isLoading ? (
          <SetSkeletonGrid />
        ) : imagesQuery.isError ? (
          <ErrorState onRetry={() => imagesQuery.refetch()} />
        ) : images.length === 0 ? (
          <EmptyState hasFilters={!isFiltersEmpty(filters)} />
        ) : viewMode === 'grouped' && groupedByShoot.length > 1 ? (
          <div className="flex flex-col gap-8">
            {groupedByShoot.map((g) => {
              const color = shootColorMap.get(g.shootId) ?? '#5fa8d3';
              return (
                <div key={g.shootId}>
                  <div
                    className="flex items-center gap-3.5 pb-2.5 mb-3 border-t border-loupe-border pt-5 first:border-t-0 first:pt-1"
                    style={{ ['--shoot-color' as string]: color }}
                  >
                    <span className="flex items-center gap-2.5 font-mono text-[11px] text-loupe-text-3 tracking-wider uppercase">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: color }}
                      />
                      {g.shootName}
                    </span>
                    <span className="flex-1 h-px bg-loupe-border" />
                    <span className="font-mono text-[11px] text-loupe-text-3">
                      {g.images.length} frames
                    </span>
                  </div>
                  <MasonryGrid>
                    {g.images.map((img, idx) => (
                      <ImageWrapper
                        key={img.id}
                        img={img}
                        idx={idx}
                        selectMode={selectMode}
                        selected={selected.has(img.id)}
                        onSelect={toggleSelect}
                        onOpen={(id) => navigate(`/sets/${setId}/images/${id}`)}
                        showSourceChip={false}
                        sourceColor={color}
                      />
                    ))}
                  </MasonryGrid>
                </div>
              );
            })}
          </div>
        ) : (
          <MasonryGrid>
            {images.map((img, idx) => (
              <ImageWrapper
                key={img.id}
                img={img}
                idx={idx}
                selectMode={selectMode}
                selected={selected.has(img.id)}
                onSelect={toggleSelect}
                onOpen={(id) => navigate(`/sets/${setId}/images/${id}`)}
                showSourceChip={viewMode === 'custom' && groupedByShoot.length > 1}
                sourceColor={shootColorMap.get(img.shootId) ?? '#5fa8d3'}
              />
            ))}
          </MasonryGrid>
        )}
      </div>

      {/* Action bar — visas i selection mode med 1+ bild vald */}
      {selectMode && selected.size > 0 ? (
        <SetActionBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onRemove={() => {
            if (window.confirm(`Remove ${selected.size} image(s) from this set? The images themselves stay in their shoots.`)) {
              removeMutation.mutate(Array.from(selected));
            }
          }}
        />
      ) : null}

      {/* Edit modal — minimal v1: delete eller cancel.
          Full PATCH-flöde (rename/color/tags) kommer i v2. */}
      {editOpen && set ? (
        <EditSetSheet
          set={set}
          onClose={() => setEditOpen(false)}
          onDelete={() => {
            if (window.confirm('Delete this set? Images stay in their shoots.')) {
              deleteSetMutation.mutate();
            }
          }}
        />
      ) : null}
    </div>
  );
}

// ─── Image wrapper (selection or plain) ──────────────────────────────

interface ImageWrapperProps {
  img: SetImage;
  idx: number;
  selectMode: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  showSourceChip: boolean;
  sourceColor: string;
}

function ImageWrapper({
  img,
  idx,
  selectMode,
  selected,
  onSelect,
  onOpen,
  showSourceChip,
  sourceColor,
}: ImageWrapperProps) {
  const aspect = img.width && img.height ? img.width / img.height : 0.66;

  if (selectMode) {
    return (
      <div
        data-aspect={aspect}
        className="relative"
        onClick={() => onSelect(img.id)}
      >
        <div
          className={[
            'relative overflow-hidden rounded-md bg-loupe-bg-2 cursor-pointer transition-transform duration-200',
            selected
              ? 'scale-[0.96] ring-2 ring-henrik shadow-[0_0_24px_-4px_rgba(95,168,211,0.5)]'
              : '',
          ].join(' ')}
        >
          <img
            src={imageUrl(img.id, 'preview')}
            alt={img.filename}
            loading="lazy"
            decoding="async"
            width={img.width}
            height={img.height}
            className="w-full h-auto block"
            style={{
              aspectRatio: img.width && img.height ? `${img.width} / ${img.height}` : '2 / 3',
            }}
          />
          <div
            className={[
              'absolute top-2 left-2 w-7 h-7 rounded-lg backdrop-blur transition-all flex items-center justify-center',
              selected
                ? 'bg-henrik border border-henrik text-white'
                : 'bg-black/50 border border-white/60 text-transparent',
            ].join(' ')}
          >
            {selected ? '✓' : ''}
          </div>
          {showSourceChip && img.shootName ? (
            <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/65 backdrop-blur border border-white/10 rounded-full text-[10px] text-white/75 inline-flex items-center gap-1 max-w-[140px]">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: sourceColor }}
              />
              <span className="truncate">{img.shootName}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-image-id={img.id} data-aspect={aspect}>
      <ImageCard
        image={img as ImageSummary}
        shootId={img.shootId}
        index={idx}
        onOpen={onOpen}
      />
      {showSourceChip && img.shootName ? (
        <div className="pointer-events-none absolute top-2 right-2 z-30 px-2 py-0.5 bg-black/65 backdrop-blur border border-white/10 rounded-full text-[10px] text-white/85 inline-flex items-center gap-1 max-w-[60%] opacity-0 group-hover:opacity-100 transition-opacity">
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: sourceColor }}
          />
          <span className="truncate">{img.shootName}</span>
        </div>
      ) : null}
    </div>
  );
}

// ─── Edit set sheet (minimal: delete + close — full edit kommer i v2) ──

function EditSetSheet({
  set,
  onClose,
  onDelete,
}: {
  set: { id: string; name: string };
  onClose: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-[420px] bg-loupe-bg-2 border border-loupe-border-2 rounded-2xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
        <div className="px-6 py-5 border-b border-loupe-border flex items-center justify-between">
          <div className="font-display text-xl font-medium tracking-tight">
            Edit <em className="italic font-normal text-loupe-text-2">{set.name}</em>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-transparent border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text transition-colors flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-6 py-5 text-sm text-loupe-text-2 leading-relaxed">
          <p className="mb-4">
            Full edit (rename, change color, manage tags) coming in next sprint. For now you can delete this set — your images stay safe in their shoots.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-loupe-border bg-black/20 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onDelete}
            className="px-4 py-2.5 bg-transparent border border-red-500/40 text-red-300 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors hover:bg-red-500/10 inline-flex items-center gap-2 min-h-[40px]"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete set
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 bg-white text-black rounded-full text-xs font-semibold tracking-wider uppercase min-h-[40px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Action bar ──────────────────────────────────────────────────────

function SetActionBar({
  count,
  onClear,
  onRemove,
}: {
  count: number;
  onClear: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="fixed bottom-3.5 sm:bottom-7 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2.5 pl-4 pr-3.5 py-3 bg-loupe-bg-2/95 backdrop-blur-2xl border border-loupe-border-2 rounded-full shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] max-w-[calc(100vw-24px)]">
      <div className="flex items-center gap-2 text-sm font-medium whitespace-nowrap">
        <strong className="font-display text-lg text-henrik tabular-nums">{count}</strong>
        <span className="hidden sm:inline">selected</span>
      </div>
      <div className="w-px h-5 bg-loupe-border-2" />
      <button
        onClick={onRemove}
        className="bg-transparent text-red-300 px-3 py-2 rounded-full text-xs font-medium flex items-center gap-2 hover:bg-red-500/10 min-h-[40px] transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Remove from set</span>
        <span className="sm:hidden">Remove</span>
      </button>
      <button
        onClick={onClear}
        aria-label="Clear selection"
        className="text-loupe-text-3 hover:text-loupe-text p-2 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function isFiltersEmpty(f: GalleryFilters): boolean {
  return f.stars === 'all' && f.user === 'all' && f.favorites === 'off';
}

const STAR_MIN_MAP: Record<StarFilter, number> = {
  all: 0,
  '1+': 1,
  '2+': 2,
  '3+': 3,
  '4+': 4,
  '5': 5,
};

function filterImages(images: SetImage[], filters: GalleryFilters): SetImage[] {
  const minStars = STAR_MIN_MAP[filters.stars];

  return images.filter((img) => {
    const ratings = img.ratings ?? {};
    const favorites = img.favorites ?? {};
    const henrikStars = ratings.henrik ?? 0;
    const michelleStars = ratings.michelle ?? 0;
    const henrikFav = favorites.henrik ?? false;
    const michelleFav = favorites.michelle ?? false;

    // Stars-filter — kollar mot vald user
    if (minStars > 0) {
      let maxRel = 0;
      if (filters.user === 'henrik') maxRel = henrikStars;
      else if (filters.user === 'michelle') maxRel = michelleStars;
      else if (filters.user === 'both') maxRel = Math.min(henrikStars, michelleStars);
      else maxRel = Math.max(henrikStars, michelleStars);

      if (filters.stars === '5') {
        if (maxRel !== 5) return false;
      } else {
        if (maxRel < minStars) return false;
      }
    }

    // Favorites-filter
    if (filters.favorites === 'on') {
      if (filters.user === 'henrik' && !henrikFav) return false;
      if (filters.user === 'michelle' && !michelleFav) return false;
      if (filters.user === 'both' && !(henrikFav && michelleFav)) return false;
      if (filters.user === 'all' && !(henrikFav || michelleFav)) return false;
    }

    return true;
  });
}

// Deterministisk färg från ett ID — vi använder ASCII-summan modulo paletten.
const FALLBACK_PALETTE = ['#5fa8d3', '#e63946', '#ffd700', '#10b981', '#8b5cf6', '#f59e0b', '#e0b3a3', '#a78bfa'];

function hashColor(id: string): string {
  let sum = 0;
  for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
  return FALLBACK_PALETTE[sum % FALLBACK_PALETTE.length];
}

// ─── States ──────────────────────────────────────────────────────────

function SetSkeletonGrid() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="bg-loupe-bg-2 rounded-md animate-pulse"
          style={{ aspectRatio: i % 5 !== 3 ? '2/3' : '3/2' }}
        />
      ))}
    </div>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
      <Layers className="w-8 h-8 text-loupe-text-3 mx-auto mb-4" />
      <div className="font-display text-2xl mb-2">
        {hasFilters ? 'No frames match these filters' : 'No frames in this set yet'}
      </div>
      <p className="text-loupe-text-2 text-sm">
        {hasFilters
          ? 'Try widening the star range or switching the user filter to All.'
          : 'Add images via Selection mode in any shoot, or use the "Add to set" button.'}
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2 text-michelle">Could not load set</div>
      <p className="text-loupe-text-2 text-sm mb-4">
        Something went wrong. Make sure the backend is running.
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider"
      >
        Retry
      </button>
    </div>
  );
}
